import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import api from './api/axiosInstance';
import Sidebar from './Sidebar';
import { Card, Row, Col, Container, Button, Image, Form, Alert, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { UserContext } from './UserContext';
import Breadcrumb from './Breadcrumb';
import icNumber from './assets/img/ic-number.svg';
import icStructure from './assets/img/ic-structure.svg';
import icNews from './assets/img/ic-news.svg';
import icStatus from './assets/img/ic-doc-refresh.svg';
import icList from './assets/img/ic-list.svg';
import icCalendar from './assets/img/ic-calendar.svg';
import icUser from './assets/img/ic-user-circle.svg';
import icDownload from './assets/img/ic-file-download.svg';
import icAdd from './assets/img/ic-add-circle.svg';
import icSuccess from './assets/img/ic-celebration-circle.svg';
import ProfileUpdateSuccessModal from './components/Profile/ProfileUpdateSuccessModal';
import { useMobileHeaderBackAction } from './HeaderActionContext';

const SupportView = () => {
    const { id } = useParams();
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ query: '(max-width: 767.98px)' });
    const handleMobileBack = useCallback(() => navigate('/support'), [navigate]);
    useMobileHeaderBackAction(handleMobileBack, isMobile);

    const [ticket, setTicket] = useState(null);
    const [activity, setActivity] = useState([]);
    const [previewUrl, setPreviewUrl] = useState(null);

    const lastReadRef = useRef(
        parseInt(localStorage.getItem(`support_read_${id}`) || '0', 10)
    );

    const [reply, setReply] = useState('');
    const [sending, setSending] = useState(false);
    const [replyError, setReplyError] = useState('');
    const [replyAttachments, setReplyAttachments] = useState([]);

    const [reopening, setReopening] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState({ type: '', title: '', description: '' });
    const [editErrors, setEditErrors] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState('');
    const [editExisting, setEditExisting] = useState([]);
    const [editNew, setEditNew] = useState([]);
    const [showEditSuccessModal, setShowEditSuccessModal] = useState(false);

    const fetchTicket = useCallback(async () => {
        try {
            const res = await api.get(`/api/support-requests/${id}`);
            const data = res.data;
            setTicket(data);
            setActivity(
                [...(data.activity || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            );
        } catch (err) {
            console.error('Error fetching support request:', err);
        }
    }, [id]);

    useEffect(() => {
        if (user && id) fetchTicket();
    }, [user, id, fetchTicket]);

    useEffect(() => {
        if (id) {
            localStorage.setItem(`support_read_${id}`, String(Date.now()));
        }
    }, [id, activity]);

    const handleReply = async () => {
        if (!reply.trim()) return;
        setSending(true);
        setReplyError('');
        try {
            const body = new FormData();
            body.append('comment', reply);
            replyAttachments.forEach(f => body.append('attachments', f));
            const res = await api.post(`/api/support-requests/${id}/reply`, body);
            setActivity(
                [...(res.data.activity || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            );
            setReply('');
            setReplyAttachments([]);
        } catch (err) {
            setReplyError(err.response?.data?.message || 'Failed to send reply. Please try again.');
        } finally {
            setSending(false);
        }
    };


    const handleDelete = async () => {
        setDeleting(true);
        setDeleteError('');
        try {
            await api.delete(`/api/support-requests/${id}`);
            navigate('/support', isMobile ? { state: { supportSuccess: 'deleted' } } : undefined);
        } catch (err) {
            setDeleteError(err.response?.data?.message || 'Failed to delete. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const handleEditOpen = () => {
        setEditForm({
            type: ticket?.ticketClass || '',
            title: ticket?.businessPurpose || '',
            description: ticket?.comment || '',
        });
        setEditExisting(ticket?.attachments || []);
        setEditNew([]);
        setEditErrors({});
        setEditError('');
        setShowEditModal(true);
    };

    const handleEditSave = async () => {
        const newErrors = {};
        if (!editForm.type) newErrors.type = 'Type is required';
        if (!editForm.title.trim()) newErrors.title = 'Title is required';
        if (!editForm.description.trim()) newErrors.description = 'Description is required';
        if (Object.keys(newErrors).length > 0) { setEditErrors(newErrors); return; }

        const MAX = 5 * 1024 * 1024;
        if (editNew.some(f => f.size > MAX)) {
            setEditError('One or more files exceed 5MB. Please remove them.');
            return;
        }

        setEditSaving(true);
        setEditError('');
        try {
            const body = new FormData();
            body.append('type', editForm.type);
            body.append('title', editForm.title);
            body.append('description', editForm.description);
            body.append('keepAttachments', JSON.stringify(editExisting));
            editNew.forEach(f => body.append('attachments', f));
            await api.put(`/api/support-requests/${id}`, body);
            setShowEditModal(false);
            await fetchTicket();
            if (isMobile) setShowEditSuccessModal(true);
        } catch (err) {
            setEditError(err.response?.data?.message || 'Failed to save. Please try again.');
        } finally {
            setEditSaving(false);
        }
    };

    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '');

    const isImageAttachment = (filename) =>
        /\.(jpg|jpeg|png)$/i.test(filename || '');

    const allAttachments = [
        ...(ticket?.attachments || []).map(a => ({ ...a, source: 'Initial request' })),
        ...activity.flatMap(entry =>
            (entry.attachments || []).map(a => ({
                ...a,
                source: entry.createdBy
                    ? `${entry.createdBy.firstName || ''} ${entry.createdBy.familyName || ''}`.trim() || 'Reply'
                    : 'Reply',
            }))
        ),
    ];

    const handleDownload = async (url, filename) => {
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(blobUrl);
        } catch {
            window.open(url, '_blank');
        }
    };

    const formatDateTime = (iso) => {
        const d = new Date(iso);
        const pad = n => String(n).padStart(2, '0');
        return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const isStudentMessage = (entry) =>
        entry.createdBy && String(entry.createdBy._id || entry.createdBy) === String(ticket?.createdBy);

    const hasAdminReplies = activity.some(a => !isStudentMessage(a));
    const isArchived = ticket?.status === 'Archived';
    const isResolved = ticket?.status === 'Resolved';
    const isClosed = isArchived || isResolved;

    const handleReopen = async () => {
        setReopening(true);
        try {
            const res = await api.post(`/api/support-requests/${id}/reopen`);
            setTicket(res.data);
            setActivity(
                [...(res.data.activity || [])].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            );
        } catch (err) {
            console.error('Reopen failed:', err);
        } finally {
            setReopening(false);
        }
    };

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1 support-detail-page">
                <Container>
                    <Row>
                        <Col>
                            <Breadcrumb label={ticket?.businessPurpose || (ticket ? `#${ticket.ticketId}` : '')} />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <Card.Body>
                                    <Row>
                                        <Col>
                                            <h1>Support request #{ticket?.ticketId}</h1>
                                        </Col>
                                    </Row>
                                    <Row>
                                        {/* Left — Request details */}
                                        <Col md={6}>
                                            <Card className="mb-4 support-request-details-card">
                                                <Card.Body>
                                                    <h3><span className="item-number">1. </span>Request details</h3>
                                                    <hr />
                                                    <Row className="mb-3 support-request-details__row">
                                                        <Col md={6}>
                                                            <div className="icon-text">
                                                                <span className="icon" aria-hidden="true">
                                                                    <Image src={icNumber} alt="ID" />
                                                                </span>
                                                                <span className="text">
                                                                    ID
                                                                    <br />
                                                                    <strong>#{ticket?.ticketId || 'N/A'}</strong>
                                                                </span>
                                                            </div>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div className="icon-text">
                                                                <span className="icon" aria-hidden="true">
                                                                    <Image src={icStructure} alt="Type" />
                                                                </span>
                                                                <span className="text">
                                                                    Type
                                                                    <br />
                                                                    <strong>{ticket?.ticketClass || 'N/A'}</strong>
                                                                </span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className="mb-3 support-request-details__row">
                                                        <Col md={6}>
                                                            <div className="icon-text">
                                                                <span className="icon" aria-hidden="true">
                                                                    <Image src={icNews} alt="Title" />
                                                                </span>
                                                                <span className="text">
                                                                    Title
                                                                    <br />
                                                                    <strong>{ticket?.businessPurpose || 'N/A'}</strong>
                                                                </span>
                                                            </div>
                                                        </Col>
                                                        <Col md={6}>
                                                            <div className="icon-text">
                                                                <span className="icon" aria-hidden="true">
                                                                    <Image src={icStatus} alt="Status" />
                                                                </span>
                                                                <span className="text">
                                                                    Status
                                                                    <br />
                                                                    <strong>{ticket?.status || 'N/A'}</strong>
                                                                </span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className="support-request-details__row">
                                                        <Col md={12}>
                                                            <div className="icon-text">
                                                                <span className="icon" aria-hidden="true">
                                                                    <Image src={icList} alt="Description" />
                                                                </span>
                                                                <span className="text">
                                                                    Description
                                                                    <br />
                                                                    <strong>{ticket?.comment || 'N/A'}</strong>
                                                                </span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    {allAttachments.length > 0 && (() => {
                                                        const images = allAttachments.filter(a => isImageAttachment(a.filename || a.url));
                                                        const files  = allAttachments.filter(a => !isImageAttachment(a.filename || a.url));
                                                        return (
                                                            <>
                                                                <div className="support-request-details__attachments-label d-md-none">
                                                                    Attachments
                                                                </div>
                                                                {images.length > 0 && (
                                                                    <Row className="mt-3">
                                                                        <Col>
                                                                            <div className="d-flex flex-wrap gap-2">
                                                                                {images.map((a, idx) => {
                                                                                    const url = `${baseUrl}${a.url}`;
                                                                                    const filename = a.originalName || a.filename || 'Download';
                                                                                    return (
                                                                                        <img
                                                                                            key={idx}
                                                                                            src={url}
                                                                                            alt={filename}
                                                                                            onClick={() => setPreviewUrl(url)}
                                                                                            style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid #c0d9ee', cursor: 'pointer', display: 'block' }}
                                                                                        />
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                )}
                                                                {files.length > 0 && (
                                                                    <Row className="mt-2">
                                                                        <Col>
                                                                            <div className="d-flex flex-wrap gap-2">
                                                                                {files.map((a, idx) => {
                                                                                    const url = `${baseUrl}${a.url}`;
                                                                                    const filename = a.originalName || a.filename || 'Download';
                                                                                    return (
                                                                                        <button
                                                                                            key={idx}
                                                                                            onClick={() => handleDownload(url, filename)}
                                                                                            className="d-inline-flex align-items-center gap-1 btn btn-link p-0"
                                                                                        >
                                                                                            <Image src={icDownload} alt="" />
                                                                                            <span style={{ fontSize: '1.2rem' }}>{filename}</span>
                                                                                        </button>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </Card.Body>
                                            </Card>
                                        </Col>

                                        {/* Right — Conversation */}
                                        <Col md={6}>
                                            <Card className="mb-4">
                                                <Card.Body>
                                                    <h3><span className="item-number">2. </span>Abroadify's responses</h3>
                                                    <hr />
                                                    <Row className="row-activity">
                                                        <Col md={12}>
                                                            {activity.length === 0 ? (
                                                                <p>No responses yet.</p>
                                                            ) : (
                                                                activity.map((entry, i) => {
                                                                    const isStudent = isStudentMessage(entry);
                                                                    const isUnread = !isStudent &&
                                                                        new Date(entry.createdAt).getTime() > lastReadRef.current;
                                                                    const byName = entry.createdBy
                                                                        ? `${entry.createdBy.firstName || ''} ${entry.createdBy.familyName || ''}`.trim()
                                                                        : isStudent ? 'You' : 'Abroadify';
                                                                    return (
                                                                        <Card
                                                                            className={`mb-3 card-activity${isStudent ? ' card-activity--student' : ''}`}
                                                                            key={i}
                                                                        >
                                                                            <Card.Body>
                                                                                <p className="mb-2">
                                                                                    <Image className="me-1" src={icCalendar} alt="" />
                                                                                    {formatDateTime(entry.createdAt)}
                                                                                    <span className="dot"></span>
                                                                                    <Image className="me-1" src={icUser} alt="" />
                                                                                    <span>{isStudent ? 'You' : byName}</span>
                                                                                </p>
                                                                                <h5 className={isUnread ? 'fw-bold' : undefined}>
                                                                                    {entry.comment}
                                                                                </h5>
                                                                                {entry.attachments?.length > 0 && (
                                                                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                                                                        {entry.attachments.map((a, aIdx) => {
                                                                                            const url = `${baseUrl}${a.url}`;
                                                                                            return isImageAttachment(a.url) ? (
                                                                                                <img
                                                                                                    key={aIdx}
                                                                                                    src={url}
                                                                                                    alt={a.originalName}
                                                                                                    onClick={() => setPreviewUrl(url)}
                                                                                                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6, border: '1px solid #c0d9ee', cursor: 'pointer', display: 'block' }}
                                                                                                />
                                                                                            ) : (
                                                                                                <button
                                                                                                    key={aIdx}
                                                                                                    onClick={() => handleDownload(url, a.originalName)}
                                                                                                    className="d-inline-flex align-items-center gap-1 btn btn-link p-0"
                                                                                                >
                                                                                                    <Image src={icDownload} alt="" />
                                                                                                    <span style={{ fontSize: '1.2rem' }}>{a.originalName}</span>
                                                                                                </button>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                )}
                                                                            </Card.Body>
                                                                        </Card>
                                                                    );
                                                                })
                                                            )}
                                                        </Col>
                                                    </Row>

                                                    {/* Reply / status section */}
                                                    {isArchived ? (
                                                        <Row className="mt-3">
                                                            <Col>
                                                                <p className="text-muted mb-0" style={{ fontSize: '1.2rem' }}>
                                                                    The request was archived on{' '}
                                                                    <strong className="font-size-md">{formatDateTime(ticket?.updatedAt)}</strong>
                                                                    {ticket?.updatedBy && (
                                                                        <> by <strong className="font-size-md">{`${ticket.updatedBy.firstName || ''} ${ticket.updatedBy.familyName || ''}`.trim()}</strong></>
                                                                    )}.
                                                                </p>
                                                            </Col>
                                                        </Row>
                                                    ) : (
                                                        <>
                                                            {replyError && <Alert variant="danger" className="mt-2">{replyError}</Alert>}
                                                            <Row className="mt-3">
                                                                <Col>
                                                                    <Form.Control
                                                                        as="textarea"
                                                                        rows={3}
                                                                        placeholder="Write your reply..."
                                                                        value={reply}
                                                                        onChange={e => setReply(e.target.value)}
                                                                        disabled={sending || isResolved}
                                                                    />
                                                                </Col>
                                                            </Row>

                                                            {isResolved ? (
                                                                <Row className="mt-2">
                                                                    <Col>
                                                                        <p className="text-muted mb-0" style={{ fontSize: '1.2rem' }}>
                                                                            The request was resolved on{' '}
                                                                            <strong className="font-size-md">{formatDateTime(ticket?.updatedAt)}</strong>
                                                                            {ticket?.updatedBy && (
                                                                                <> by <strong className="font-size-md">{`${ticket.updatedBy.firstName || ''} ${ticket.updatedBy.familyName || ''}`.trim()}</strong></>
                                                                            )}.
                                                                        </p>
                                                                    </Col>
                                                                </Row>
                                                            ) : (
                                                                <Row className="mt-2">
                                                                    <Col>
                                                                        <Form.Group controlId="replyAttachments">
                                                                            <Form.Label>Attach files <span className="text-muted fw-normal">(optional)</span></Form.Label>
                                                                            {replyAttachments.length < 3 && (
                                                                                <div
                                                                                    className="custom-upload-box mb-1"
                                                                                    onClick={() => document.getElementById('replyFileInput').click()}
                                                                                >
                                                                                    <Image className="upload-icon" src={icAdd} alt="Attach" />
                                                                                    <span className="upload-text">
                                                                                        Choose file{replyAttachments.length > 0 && ` (${replyAttachments.length}/3)`}
                                                                                    </span>
                                                                                </div>
                                                                            )}
                                                                            <Form.Control
                                                                                id="replyFileInput"
                                                                                type="file"
                                                                                accept=".jpg,.jpeg,.png,.pdf"
                                                                                multiple
                                                                                style={{ display: 'none' }}
                                                                                onChange={(e) => {
                                                                                    const added = Array.from(e.target.files);
                                                                                    setReplyAttachments(prev => [...prev, ...added].slice(0, 3));
                                                                                    e.target.value = '';
                                                                                }}
                                                                            />
                                                                            <p className="info">JPG, PNG or PDF. Max 5MB each. Up to 3 files.</p>
                                                                            {replyAttachments.length > 0 && (() => {
                                                                                const MAX = 5 * 1024 * 1024;
                                                                                const hasOversized = replyAttachments.some(f => f.size > MAX);
                                                                                return (
                                                                                    <>
                                                                                        <div className="d-flex flex-wrap gap-2 mt-2">
                                                                                            {replyAttachments.map((file, idx) => {
                                                                                                const isImg = /\.(jpg|jpeg|png)$/i.test(file.name);
                                                                                                const tooLarge = file.size > MAX;
                                                                                                return (
                                                                                                    <div key={idx} style={{ position: 'relative' }}>
                                                                                                        {isImg ? (
                                                                                                            <Image src={URL.createObjectURL(file)} width={64} height={64} style={{ objectFit: 'cover', borderRadius: 6, border: tooLarge ? '2px solid #dc3545' : '1px solid #c0d9ee', opacity: tooLarge ? 0.6 : 1, display: 'block' }} />
                                                                                                        ) : (
                                                                                                            <div style={{ width: 64, height: 64, borderRadius: 6, border: tooLarge ? '2px solid #dc3545' : '1px solid #c0d9ee', background: '#f0f8ff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 4, opacity: tooLarge ? 0.6 : 1 }}>
                                                                                                                <span style={{ fontSize: 9, wordBreak: 'break-all', textAlign: 'center', lineHeight: 1.2 }}>{file.name}</span>
                                                                                                            </div>
                                                                                                        )}
                                                                                                        {tooLarge && (
                                                                                                            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(220,53,69,0.85)', color: '#fff', fontSize: 9, textAlign: 'center', borderRadius: '0 0 6px 6px', padding: '1px 0', lineHeight: 1.4 }}>Too large</div>
                                                                                                        )}
                                                                                                        <button type="button" onClick={() => setReplyAttachments(prev => prev.filter((_, i) => i !== idx))} style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: '50%', background: '#dc3545', border: 'none', color: '#fff', fontSize: 11, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                        {hasOversized && <p className="text-danger mt-2 mb-0" style={{ fontSize: '1.2rem' }}>Some files exceed 5MB. Please remove them before sending.</p>}
                                                                                    </>
                                                                                );
                                                                            })()}
                                                                        </Form.Group>
                                                                    </Col>
                                                                </Row>
                                                            )}

                                                            <Row className="mt-2">
                                                                <Col className="text-end">
                                                                    {isResolved ? (
                                                                        <Button variant="outline-primary" size="sm" onClick={handleReopen} disabled={reopening}>
                                                                            {reopening ? 'Reopening…' : 'Reopen request'}
                                                                        </Button>
                                                                    ) : (
                                                                        <Button variant="primary" size="sm" onClick={handleReply} disabled={sending || !reply.trim() || replyAttachments.some(f => f.size > 5 * 1024 * 1024)}>
                                                                            {sending ? 'Sending…' : 'Send reply'}
                                                                        </Button>
                                                                    )}
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    <Row className="text-end mb-3">
                                        <Col md={12}>
                                            <Button variant="primary" onClick={() => navigate('/support')}>
                                                Close
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    {!hasAdminReplies && ticket && (
                        <Row className="buttons-row">
                            <Col>
                                <Button variant="outline-secondary" onClick={() => setShowDeleteModal(true)}>
                                    Delete
                                </Button>
                                <Button variant="outline-primary" onClick={handleEditOpen}>
                                    Edit
                                </Button>
                            </Col>
                        </Row>
                    )}
                </Container>
            </main>

            {/* Delete confirmation modal */}
            <Modal
                className={isMobile ? 'discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen support-mobile-delete-modal' : undefined}
                show={showDeleteModal}
                onHide={() => { setShowDeleteModal(false); setDeleteError(''); }}
                centered={!isMobile}
                fullscreen={isMobile}
            >
                {isMobile ? (
                    <div className="discovery-filter-mobile-panel">
                        <div className="discovery-filter-mobile-panel__header">
                            <Modal.Title>Delete support request</Modal.Title>
                            <div className="discovery-filter-mobile-panel__header-actions">
                                <button
                                    type="button"
                                    className="discovery-filter-mobile-panel__close"
                                    aria-label="Close delete confirmation"
                                    onClick={() => { setShowDeleteModal(false); setDeleteError(''); }}
                                >&times;</button>
                            </div>
                        </div>
                        <Modal.Body>
                            {deleteError && <Alert variant="danger">{deleteError}</Alert>}
                            Are you sure you want to delete this support request? This cannot be undone.
                        </Modal.Body>
                        <div className="discovery-filter-mobile-panel__footer">
                            <Button variant="outline-secondary" onClick={() => { setShowDeleteModal(false); setDeleteError(''); }}>
                                Cancel
                            </Button>
                            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                                {deleting ? 'Deleting…' : 'Delete'}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <Modal.Header closeButton>
                            <Modal.Title>Delete support request</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {deleteError && <Alert variant="danger">{deleteError}</Alert>}
                            Are you sure you want to delete this support request? This cannot be undone.
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="outline-secondary" className="me-3" onClick={() => { setShowDeleteModal(false); setDeleteError(''); }}>
                                Cancel
                            </Button>
                            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                                {deleting ? 'Deleting…' : 'Delete'}
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>

            {/* Edit modal */}
            <Modal
                className={isMobile ? 'with-form discovery-filter-mobile-modal discovery-filter-mobile-modal--fullscreen support-mobile-form-modal' : 'with-form'}
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                centered={!isMobile}
                fullscreen={isMobile}
            >
                <Form
                    onSubmit={e => { e.preventDefault(); handleEditSave(); }}
                    className={isMobile ? 'discovery-filter-mobile-panel' : undefined}
                >
                    {isMobile ? (
                        <div className="discovery-filter-mobile-panel__header">
                            <Modal.Title>Edit support request</Modal.Title>
                            <div className="discovery-filter-mobile-panel__header-actions">
                                <button
                                    type="button"
                                    className="discovery-filter-mobile-panel__close"
                                    aria-label="Close support request form"
                                    onClick={() => setShowEditModal(false)}
                                >&times;</button>
                            </div>
                        </div>
                    ) : (
                        <Modal.Header closeButton>
                            <Modal.Title>Edit support request</Modal.Title>
                        </Modal.Header>
                    )}
                    <Modal.Body>
                        {editError && <Alert variant="danger">{editError}</Alert>}
                        <Form.Group className="mb-3">
                            <Form.Label>Type <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                value={editForm.type}
                                onChange={e => setEditForm(f => ({ ...f, type: e.target.value }))}
                                isInvalid={!!editErrors.type}
                            >
                                <option value="">Please select</option>
                                {['Incident', 'Support service request', 'Advisory service request'].map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">{editErrors.type}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                value={editForm.title}
                                onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                                isInvalid={!!editErrors.title}
                            />
                            <Form.Control.Feedback type="invalid">{editErrors.title}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                value={editForm.description}
                                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                                isInvalid={!!editErrors.description}
                            />
                            <Form.Control.Feedback type="invalid">{editErrors.description}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Attachments</Form.Label>
                            {(() => {
                                const MAX = 5 * 1024 * 1024;
                                const total = editExisting.length + editNew.length;
                                const hasOversized = editNew.some(f => f.size > MAX);
                                return (
                                    <>
                                        {total < 3 && (
                                            <div
                                                className="custom-upload-box mb-1"
                                                onClick={() => document.getElementById('editFileInput').click()}
                                            >
                                                <Image className="upload-icon" src={icAdd} alt="Upload file" />
                                                <span className="upload-text">
                                                    Choose file{total > 0 && ` (${total}/3)`}
                                                </span>
                                            </div>
                                        )}
                                        <Form.Control
                                            id="editFileInput"
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                                            multiple
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const added = Array.from(e.target.files);
                                                setEditNew(prev => [...prev, ...added].slice(0, 3 - editExisting.length));
                                                e.target.value = '';
                                            }}
                                        />
                                        <p className="info">JPG, PNG, PDF or Word. Max 5MB each. Up to 3 files.</p>
                                        {total > 0 && (
                                            <div className="d-flex flex-wrap gap-2 mt-2">
                                                {/* Existing saved attachments */}
                                                {editExisting.map((a, idx) => (
                                                    <div key={`ex-${idx}`} style={{ position: 'relative' }}>
                                                        {isImageAttachment(a.filename) ? (
                                                            <Image
                                                                src={`${baseUrl}${a.url}`}
                                                                width={64} height={64}
                                                                style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #c0d9ee', display: 'block' }}
                                                            />
                                                        ) : (
                                                            <div style={{
                                                                width: 64, height: 64, borderRadius: 6,
                                                                border: '1px solid #c0d9ee', background: '#f0f8ff',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                padding: 4,
                                                            }}>
                                                                <span style={{ fontSize: 9, wordBreak: 'break-all', textAlign: 'center', lineHeight: 1.2 }}>
                                                                    {a.originalName || a.filename}
                                                                </span>
                                                            </div>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditExisting(prev => prev.filter((_, i) => i !== idx))}
                                                            style={{
                                                                position: 'absolute', top: -6, right: -6,
                                                                width: 18, height: 18, borderRadius: '50%',
                                                                background: '#dc3545', border: 'none', color: '#fff',
                                                                fontSize: 11, lineHeight: 1, cursor: 'pointer',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}
                                                        >×</button>
                                                    </div>
                                                ))}
                                                {/* New files */}
                                                {editNew.map((file, idx) => {
                                                    const isImg = /\.(jpg|jpeg|png)$/i.test(file.name);
                                                    const tooLarge = file.size > MAX;
                                                    return (
                                                        <div key={`new-${idx}`} style={{ position: 'relative' }}>
                                                            {isImg ? (
                                                                <Image
                                                                    src={URL.createObjectURL(file)}
                                                                    width={64} height={64}
                                                                    style={{
                                                                        objectFit: 'cover', borderRadius: 6, display: 'block',
                                                                        border: tooLarge ? '2px solid #dc3545' : '1px solid #c0d9ee',
                                                                        opacity: tooLarge ? 0.6 : 1,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div style={{
                                                                    width: 64, height: 64, borderRadius: 6,
                                                                    border: tooLarge ? '2px solid #dc3545' : '1px solid #c0d9ee',
                                                                    background: '#f0f8ff', display: 'flex',
                                                                    alignItems: 'center', justifyContent: 'center',
                                                                    padding: 4, opacity: tooLarge ? 0.6 : 1,
                                                                }}>
                                                                    <span style={{ fontSize: 9, wordBreak: 'break-all', textAlign: 'center', lineHeight: 1.2 }}>
                                                                        {file.name}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {tooLarge && (
                                                                <div style={{
                                                                    position: 'absolute', bottom: 0, left: 0, right: 0,
                                                                    background: 'rgba(220,53,69,0.85)', color: '#fff',
                                                                    fontSize: 9, textAlign: 'center',
                                                                    borderRadius: '0 0 6px 6px', padding: '1px 0', lineHeight: 1.4,
                                                                }}>Too large</div>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => setEditNew(prev => prev.filter((_, i) => i !== idx))}
                                                                style={{
                                                                    position: 'absolute', top: -6, right: -6,
                                                                    width: 18, height: 18, borderRadius: '50%',
                                                                    background: '#dc3545', border: 'none', color: '#fff',
                                                                    fontSize: 11, lineHeight: 1, cursor: 'pointer',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                }}
                                                            >×</button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {hasOversized && (
                                            <p className="text-danger mt-2 mb-0" style={{ fontSize: '1.2rem' }}>
                                                Some files exceed 5MB. Please remove them before saving.
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                        </Form.Group>
                    </Modal.Body>
                    {isMobile ? (
                        <div className="discovery-filter-mobile-panel__footer support-mobile-form-modal__footer">
                            <Button variant="primary" type="submit" disabled={editSaving || editNew.some(f => f.size > 5 * 1024 * 1024)}>
                                {editSaving ? 'Saving…' : 'Save'}
                            </Button>
                        </div>
                    ) : (
                        <Modal.Footer>
                            <Button className="me-3" variant="outline-secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                            <Button variant="primary" type="submit" disabled={editSaving || editNew.some(f => f.size > 5 * 1024 * 1024)}>
                                {editSaving ? 'Saving…' : 'Save'}
                            </Button>
                        </Modal.Footer>
                    )}
                </Form>
            </Modal>

            {isMobile && (
                <ProfileUpdateSuccessModal
                    show={showEditSuccessModal}
                    onHide={() => setShowEditSuccessModal(false)}
                    title="Request edited"
                    message="Your support request was successfully updated."
                    icon={icSuccess}
                />
            )}

            <Modal show={!!previewUrl} onHide={() => setPreviewUrl(null)} centered size="lg">
                <Modal.Header closeButton />
                <Modal.Body className="text-center p-2">
                    <img
                        src={previewUrl}
                        alt="Preview"
                        style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 6 }}
                    />
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button variant="outline-secondary" size="sm" onClick={() => handleDownload(previewUrl, previewUrl?.split('/').pop())}>
                        Download
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default SupportView;
